import { CustomType, GoalId, GoalWithProgress, ProgressType } from '../../services/api.types'
import { getCustomTypeWithLegacy } from '../../services/CustomTypes'
import { convertDateToTallinnTimeZone, convertUTCToLocalDate } from './date'
import { getProgress, refreshChallengeResults } from '../../views/Progress/store/ProgressActions'
import { store } from '../../index'
import { PointsLogic } from '../../views/ChallengeAdmin/components/ChallengePoints/ChallengePoints'
import { isActivityIndexType, isCollectiveType } from './challenges'
import dayjs from '../../dayjsConfig'

type SyncChallengeProgressParams = {
  goal: GoalWithProgress
  manualEntryProgressType?: CustomType
  startDate?: string
  endDate?: string
  participating?: boolean
  manualEntryEnabled?: boolean
  pointsLogic?: PointsLogic[] | null
  isPoints?: boolean
  isMeView?: boolean
  groupSum?: boolean
  token?: string
  isLegacyRanking?: boolean
  showLegacyProgress?: boolean
}

export const syncChallengeProgress = ({
  goal,
  manualEntryProgressType,
  startDate,
  endDate,
  participating,
  manualEntryEnabled,
  pointsLogic,
  isPoints,
  isMeView,
  groupSum,
  token,
  isLegacyRanking,
  showLegacyProgress,
}: SyncChallengeProgressParams) => {
  const { id: goalId, customType, progressType, splitRankingView } = goal
  // Keep derived flags centralized so both callers behave the same.
  const isAnyActivityIndex = isActivityIndexType(progressType)
  const isCollective = isCollectiveType(progressType)
  // Legacy progress is only needed for activity index challenges.
  const resolvedShowLegacyProgress = showLegacyProgress ?? isAnyActivityIndex
  // Manual entry progress type is required for activity index challenges.
  const resolvedManualEntryProgressType = manualEntryProgressType || customType || CustomType.STEPS
  // Points logic is stored as JSON string in the goal; parse only when needed.
  const resolvedIsPoints = isPoints ?? customType === CustomType.POINTS
  const resolvedPointsLogic = pointsLogic ?? (resolvedIsPoints ? (JSON.parse(goal.pointsLogic) as PointsLogic[]) : null)
  // Prefer explicit args, but fall back to the goal for backward compatibility.
  const resolvedParticipating = participating ?? goal.participating
  const resolvedManualEntryEnabled = manualEntryEnabled ?? goal.manualEntryEnabled
  // Use the same local-date formatting as the legacy flow.
  const resolvedStartDate =
    startDate ||
    dayjs(convertUTCToLocalDate(new Date(goal.startDate)))
      .locale('en')
      .format('YYYY-MM-DD')
  const resolvedEndDate =
    endDate ||
    dayjs(convertUTCToLocalDate(new Date(goal.endDate)))
      .locale('en')
      .format('YYYY-MM-DD')

  // Keep this aligned with legacy manual entry flow. It must use the manual entry progress type
  // when the challenge is an activity index, otherwise it uses the goal progress type.
  const defaultProgressType = (isAnyActivityIndex ? resolvedManualEntryProgressType : progressType) as ProgressType
  // Emit via store to keep callers simple and consistent.
  store.dispatch(
    refreshChallengeResults({
      goalId,
      customType: getCustomTypeWithLegacy(customType, defaultProgressType),
      startDate: resolvedStartDate,
      endDate: resolvedEndDate,
      participating: resolvedParticipating,
      isCollective,
      preventListUpdate: !isLegacyRanking,
      isActivityIndex: isAnyActivityIndex,
      manualEntryEnabled: resolvedManualEntryEnabled,
      // TODO:
      pointsLogicId: null,
      pointsLogic: resolvedPointsLogic,
      isPoints: resolvedIsPoints,
      isMeView,
      groupSum,
      token,
      oldSplitRankingView: isLegacyRanking && !splitRankingView,
    }),
  )
  if (resolvedShowLegacyProgress) {
    const startDate = convertDateToTallinnTimeZone(new Date(goal.startDate))
    const endDate = convertDateToTallinnTimeZone(new Date(goal.endDate))
    store.dispatch(getProgress({ startDate, endDate }))
  }
}
